import Container from '@components/styled/Container'
import HR from '@components/HR'
import Head from 'next/head'
import Link from 'next/link'
import Marquee from '@components/Marquee'
import Section from '@components/Section'
import SectionHeading from '@components/Headings/SectionHeading'
import Team from '@components/Team/Team'
import Tile from '@components/Tile'
import Text from '@components/Text'
import { NextPage } from 'next'
import { AccordionContainer, AccordionItem } from '@components/Accordion'
import PageHeader from '@components/PageHeader'
import { Grid as BoxesGrid, Item as BoxesGridItem } from '@components/BoxesGrid'
import ScrolledSentences from '@components/ScrolledSentences'
import { Slider as ProcessSlider } from '@components/Process'
import ImagesMarquee from '@components/ImagesMarquee'
import { ContainerSlim } from '@components/styled'
import { H4 } from '@components/Heading'
import Button from '@components/Button'

const AboutUsPage: NextPage = () => (
  <>
    <Head>
      <title>About us</title>
    </Head>
    <PageHeader
      title={[
        ["It's not", 'about', 'technology.'],
        ["It's", 'about', 'people'],
      ]}
    />

    <div className="mb-80 mb-md-200">
      <ImagesMarquee />
    </div>

    <ContainerSlim>
      <Text className="mt-0 mb-80 mb-md-160" size="big" color="gray">
        We have been operating on the Polish market since 2010. The company with
        only two people then grew into a professional software house. We keep up
        with the current trends, develop and grow, searching for new solutions.
        Don’t stop us now. We have completed more than 1000 projects, so we are
        able to help you, wherever you are in developing your business.
      </Text>

      <HR className="mb-80 mb-md-160" />

      <div style={{ maxWidth: 640 }}>
        <H4 className="mb-8">Who are we?</H4>
        <Text className="mb-48" color="gray" size="large">
          We could mention agan that we are professionals
        </Text>
        <Text className="mb-32">
          But we prefer to emphasise what there is that makes the projects we
          manage a success. Our team includes a range of diverse personalities
          and that is the best thing about it.
        </Text>
        <Text className="mb-80 mb-md-200">
          We know our strengths and our weaknesses, we can work together and
          know who should work on any given project to complete it efficiently.
          We are a team of people for whom feedback, responsibility and
          relationships are important. Each one of us is different, yet all of
          us are equals.
        </Text>
      </div>
    </ContainerSlim>

    <img className="mb-80 mb-md-200" src="images/group-photo.jpg" alt="" />

    <Container>
      <div style={{ maxWidth: 680 }}>
        <H4 className="mb-24">Who are we?</H4>
        <Text className="mb-80 mb-md-112" color="gray" size="large">
          We believe that the combination of our skills, well-known technology
          and clear communication is the key to success. Behind every good
          project is a team that is just as good. People help people.
        </Text>
      </div>

      <BoxesGrid className="mb-80 mb-md-200" layout="simple">
        <BoxesGridItem
          icon={<img src="icons/set-1/partnership.svg" alt="" />}
          title="Partnership"
          description="We help you to transform your idea into a finished product. We know that the combination of your vision and our solutions is the best way to make it real."
        />
        <BoxesGridItem
          icon={<img src="icons/set-1/communication.svg" alt="" />}
          title="Communication"
          description="Good communication allows us to manage the project efficiently and prevent possible problems. Let’s talk, we want to understand you and advise you as best as we can."
        />
        <BoxesGridItem
          icon={<img src="icons/set-1/individual-approach.svg" alt="" />}
          title="Individual approach"
          description="We choose technology and organize our work around specific tasks. Our company goes above and beyond known and tested schemes."
        />
        <BoxesGridItem
          icon={<img src="icons/set-1/transparency.svg" alt="" />}
          title="Transparency"
          description="We speak frankly and hope you do the same with us. We always give you clear information about the stage of the project."
        />
      </BoxesGrid>
    </Container>

    <Section>
      <Container>
        <div>
          <HR className="mb-24" />
          <SectionHeading title="Our Process" />
        </div>
      </Container>

      <ProcessSlider />
    </Section>
    <Section style={{ paddingTop: 0 }}>
      <Container>
        <div>
          <HR className="mb-24" />
          <SectionHeading title="Technologies and tools" />
        </div>
      </Container>
      <Marquee componentWidth="200px" reverse>
        <Tile
          imageUrl="images/logos/technologies/bootstrap.svg"
          title="Bootstrap"
        />
        <Tile imageUrl="images/logos/technologies/css.svg" title="CSS" />
        <Tile imageUrl="images/logos/technologies/docker.svg" title="Docker" />
        <Tile imageUrl="images/logos/technologies/gitlab.svg" title="GitLab" />
        <Tile imageUrl="images/logos/technologies/html.svg" title="HTML" />
        <Tile
          imageUrl="images/logos/technologies/invision.svg"
          title="Invision"
        />
        <Tile imageUrl="images/logos/technologies/jira.svg" title="Jira" />
      </Marquee>
      <div style={{ marginBottom: '1.6rem' }} />
      <Marquee componentWidth="200px">
        <Tile key="JS" imageUrl="images/logos/technologies/js.svg" title="JS" />
        <Tile imageUrl="images/logos/technologies/mysql.svg" title="MYSQL" />
        <Tile imageUrl="images/logos/technologies/php.svg" title="PHP" />
        <Tile
          imageUrl="images/logos/technologies/rabbitMQ.svg"
          title="rabbitMQ"
        />
        <Tile imageUrl="images/logos/technologies/react.svg" title="React" />
        <Tile imageUrl="images/logos/technologies/sketch.svg" title="Sketch" />
        <Tile imageUrl="images/logos/technologies/slack.svg" title="Slack" />
        <Tile
          imageUrl="images/logos/technologies/symfony.svg"
          title="Symfony"
        />
        <Tile imageUrl="images/logos/technologies/uxpin.svg" title="UXPin" />
      </Marquee>
    </Section>

    <div className="mb-80 mb-md-160">
      <ScrolledSentences
        title="What are we like?"
        unchangedWord="We"
        sentences={[
          'constantly developing',
          'don’t work after hours',
          'share our knowledge',
          'are counting on people',
          'like to play table football',
          'take care of the details',
        ]}
      />
    </div>

    <Container>
      <div className="mb-80 mb-md-160">
        <Team />
      </div>

      <HR className="mb-80 mb-md-160" />

      <div style={{ maxWidth: 680 }}>
        <H4 className="mb-24">Work with us!</H4>
        <Text className="mb-80 mb-md-112" color="gray" size="large">
          We do not expect cover letters or a beautiful picture on your CV.
          Don’t even need to have a master’s’. Your portfolio and practical
          skills are the measure of the value you bring to our team.
        </Text>
      </div>

      <div className="mb-80 mb-md-160">
        <AccordionContainer>
          <AccordionItem title="SEO/SEM Specialist">
            <Text>
              W związku z rozwojem naszej Agencji Interaktywnej szukamy
              ambitnego i zdolnego specjalisty ds. SEO/SEM, chcącego dołączyć do
              naszego zespołu.
            </Text>
            <Link passHref href="/career/seosem-specialist">
              <a className="d-inline-block">
                <Button text="Read more" />
              </a>
            </Link>
          </AccordionItem>
          <AccordionItem title="Front-end / Wordpress Developer">
            <Text>
              W związku z rozwojem naszej firmy, szukamy ambitnego i zdolnego
              Front-end developera, chcącego zasilić szeregi naszego zespołu.
            </Text>
            <Link passHref href="/career/frontend-developer">
              <a className="d-inline-block">
                <Button text="Read more" />
              </a>
            </Link>
          </AccordionItem>
          <AccordionItem title="Back-end Developer">
            <Text>
              W związku z rozwojem naszej firmy, szukamy ambitnego i zdolnego
              Back-end developera, chcącego zasilić szeregi naszego zespołu.
            </Text>
            <Link passHref href="/career/backend-developer">
              <a className="d-inline-block">
                <Button text="Read more" />
              </a>
            </Link>
          </AccordionItem>
        </AccordionContainer>
      </div>
    </Container>
  </>
)

export default AboutUsPage
